/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import useReverseGeocode from '../../hooks/useReverseGeocode';
import { hasActiveSubscription, formatSubscriptionLevel, formatExpiryDate } from '../../utils/subscriptionUtils';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import InvitationsOffcanvas from './InvitationsOffcanvas';
import { useTranslation } from 'react-i18next';
import PaymentService from '../../services/paymentService';
import { updateAuth } from '../../store/slices/authSlice';
import { toast } from 'react-toastify';

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);
  const { address, loading: locationLoading } = useReverseGeocode(authState.location);
  const { t } = useTranslation();
  const [showInvitations, setShowInvitations] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const verifyingRef = useRef(false);

  const handleShowInvitations = () => setShowInvitations(true);
  const handleCloseInvitations = () => setShowInvitations(false);

  // Calculer l'âge à partir de la date de naissance
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Calculer le pourcentage de complétion du profil
  const calculateCompletion = () => {
    const requiredFields = {
      name: authState.name,
      doB: authState.doB,
      gender: authState.gender,
      genderSearch: authState.genderSearch,
      LookingFor: authState.LookingFor,
      photos: authState.photos?.length >= 2,
      location: authState.location
    };

    const totalFields = Object.keys(requiredFields).length;
    const completedFields = Object.values(requiredFields).filter(Boolean).length;
    return Math.round((completedFields / totalFields) * 100);
  };

  const percentage = calculateCompletion();
  const radius = 35;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  const fetchUserData = async (update) => {
    try {
      dispatch(updateAuth(update));
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données:', error);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paymentToken = queryParams.get('token');

    const verifyPayment = async (token) => {
      // Si déjà en cours de vérification, ne pas relancer
      if (verifyingRef.current) return;
      
      try {
        verifyingRef.current = true;
        setVerifyingPayment(true);
        const result = await PaymentService.verifyPayment(token);
        console.log(result);
        if (result.status === 'success') {
          // Rafraîchir les données du profil
          await fetchUserData(result.data.update);
         
          // Nettoyer l'URL
          navigate('/profile', { replace: true });
          
          // Afficher une notification de succès adaptée au type de paiement
          const successMessage = result.data.data.type === 'invitations' 
            ? t('Vos invitations ont été ajoutées avec succès à votre compte !')
            : t('Paiement vérifié avec succès ! Votre abonnement est maintenant actif.');
            
          toast.success(successMessage, {
            position: "top-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        } else {
          // Afficher une notification d'erreur
          toast.error(t('La vérification du paiement a échoué. Veuillez réessayer ou contacter le support.'), {
            position: "top-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du paiement:', error);
        // Afficher une notification d'erreur
        toast.error(t('Une erreur est survenue lors de la vérification du paiement. Veuillez réessayer.'), {
          position: "top-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setVerifyingPayment(false);
        verifyingRef.current = false;
      }
    };

    if (paymentToken) {
      verifyPayment(paymentToken);
    }

    const swiper = new Swiper('.subscription-swiper', {
      modules: [Navigation, Pagination],
      slidesPerView: 1,
      spaceBetween: 30,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    });

    return () => {
      if (swiper) swiper.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, t]);

  return (
    <div className="page-wrapper has-menubar">
      {verifyingPayment && (
        <div className="payment-verification-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Vérification du paiement...</span>
          </div>
          <p className="mt-2">Vérification du paiement en cours...</p>
        </div>
      )}
      {/* Header */}
      <header className="header header-fixed bg-white border-0 style-2">
        <div className="container">
          <div className="header-content">
            <div className="left-content header-logo logo-lg">
              <a href="/">
                <img src={require('../../assets/images/fulllogo_transparent_nobuffer.png')} alt="" />
              </a>
            </div>
            <div className="mid-content">
            </div>
            <div className="right-content">
              <a className="font-22" onClick={() => navigate('/settings')}>
                <i className="flaticon flaticon-computer-security-shield"></i>
              </a>  
            </div>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <div className="content-body">
        <div className="container pt-0"> 
          <div className="profile-area">
            <div className="main-profile">
              <div className="about-profile">
                <a onClick={() => navigate('/settings')} className="setting dz-icon">
                  <i className="flaticon flaticon-setting"></i>
                </a>
                <div className="media rounded-circle">
                  <img 
                    src={authState.photos[0]?.photoUrl || process.env.PUBLIC_URL + '/assets/img/user/pic1.jpg'} 
                    alt="profile" 
                  />
                  <svg className="radial-progress" viewBox="0 0 80 80">
                    <circle
                      className="incomplete"
                      cx="40"
                      cy="40"
                      r={radius}
                      strokeDasharray={circumference}
                      strokeDashoffset="0"
                    />
                    <circle
                      className="complete"
                      cx="40"
                      cy="40"
                      r={radius}
                      strokeDasharray={circumference}
                      strokeDashoffset={offset}
                    />
                  </svg>
                  <div className="data-fill style-2">
                    <span>{t('profile.completion', { percentage })}</span>
                  </div>
                </div>
                <a onClick={() => navigate('/edit-profile')} className="edit-profile dz-icon">
                  <i className="flaticon flaticon-pencil-2"></i>
                </a>
              </div>
              <div className="profile-detail">
                <h4 className="name">
                  {authState.name}{authState.doB ? t('profile.age', { age: calculateAge(authState.doB) }) : ''}
                </h4>
                {!locationLoading && address && (
                  <p className="mb-0">
                    <i className="icon feather icon-map-pin me-1"></i>
                    {address}
                  </p>
                )}
                {hasActiveSubscription(authState) && (
                  <div className="subscription-info mt-2">
                    <p className="mb-0">
                      <i className="icon feather icon-award me-1"></i>
                      {t('profile.subscription.status', { 
                        level: formatSubscriptionLevel(authState.level)
                      })}
                      <span className="text-muted ms-2">
                        {t('profile.subscription.expiry', { 
                          date: formatExpiryDate(authState.packageEndDate)
                        })}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="row g-2 mb-5">
              <div className="col-4">
                <div className="card style-2">
                  <div className="card-body" onClick={handleShowInvitations}>
                      <div className="card-icon">
                        <i className="flaticon flaticon-star-1"></i>
                      </div>
                      <div className="card-content">
                        <p>{t('profile.invitations', { count: authState.invitations })}</p>
                      </div>
                      <i className="icon feather icon-plus"></i>
                  </div>
                </div>
              </div>
              <div className="col-4">
                <div className="card style-2">
                  <div className="card-body">
                    <a onClick={() => navigate('/subscription', { 
                      state: { 
                        selectedPlan: 'plus',
                        fromProfile: true
                      }
                    })}>
                      <div className="card-icon">
                        <i className="flaticon flaticon-bell"></i>
                      </div>
                      <div className="card-content">
                        <p>{t('profile.subscriptions')}</p>
                      </div>
                      <i className="icon feather icon-plus"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="swiper subscription-swiper">
              <div className="swiper-wrapper">
                <div className="swiper-slide">
                  <div className="dz-content">
                    <h5 className="title">{t('profile.plans.plus.title')}</h5>
                    <p>{t('profile.plans.plus.description')}</p>
                    <a onClick={() => navigate('/subscription', { 
                      state: { 
                        selectedPlan: 'plus',
                        fromProfile: true
                      }
                    })} className="btn rounded-xl">{t('profile.plans.plus.cta')}</a>
                  </div>
                </div>
                <div className="swiper-slide">
                  <div className="dz-content">
                    <h5 className="title">{t('profile.plans.gold.title')}</h5>
                    <p>{t('profile.plans.gold.description')}</p>
                    <a onClick={() => navigate('/subscription', { 
                      state: { 
                        selectedPlan: 'gold',
                        fromProfile: true
                      }
                    })} className="btn rounded-xl">{t('profile.plans.gold.cta')}</a>
                  </div>
                </div>
                <div className="swiper-slide">
                  <div className="dz-content">
                    <h5 className="title">{t('profile.plans.platinum.title')}</h5>
                    <p>{t('profile.plans.platinum.description')}</p>
                    <a onClick={() => navigate('/subscription', { 
                      state: { 
                        selectedPlan: 'platinum',
                        fromProfile: true
                      }
                    })} className="btn rounded-xl">{t('profile.plans.platinum.cta')}</a>
                  </div>
                </div>
              </div>
              <div className="swiper-btn">
                <div className="swiper-pagination style-1 flex-1"></div>
              </div>
            </div>
          </div>
        </div> 
      </div>
      <InvitationsOffcanvas 
        show={showInvitations} 
        onClose={handleCloseInvitations} 
      />
    </div>
  );
};

export default Profile;
